import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { nb } from "date-fns/locale";

function pad(n) {
  return n < 10 ? `0${n}` : n;
}

const HOURS = Array.from({ length: 24 }, (_, i) => `${pad(i)}:00`);
const BUFFER_MILLIS = 2 * 60 * 60 * 1000;
const HOUR = 60 * 60 * 1000;

function parseLocalDateTime(value) {
  if (value instanceof Date) return value;
  if (typeof value !== "string") return new Date(value);

  const m = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/
  );

  if (!m) return new Date(value);

  const [, y, mo, d, h, mi, s] = m;
  return new Date(+y, +mo - 1, +d, +h, +mi, s ? +s : 0, 0);
}

function getHourDate(date, hour) {
  const [h, m] = hour.split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m || 0, 0, 0);
  return d;
}

function ceilDiv(a, b) {
  return Math.ceil(a / b);
}

function isWeekendPackage(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e <= s) return false;

  const sDay = s.getDay();
  if (sDay !== 5 && sDay !== 6) return false;

  const friday16 = new Date(s);
  if (sDay === 6) friday16.setDate(friday16.getDate() - 1);
  friday16.setHours(16, 0, 0, 0);
  if (s < friday16) return false;

  const sunday = new Date(friday16);
  sunday.setDate(sunday.getDate() + 2);

  if (e.getDay() !== 0) return false;
  const sunday00 = new Date(sunday);
  sunday00.setHours(0, 0, 0, 0);
  const sunday22 = new Date(sunday);
  sunday22.setHours(22, 0, 0, 0);

  return e >= sunday00 && e <= sunday22;
}

export default function BookingWidget({
  product,
  onAddToCart,
  fetchBookings,
  checkAvailability,
}) {
  const [bookings, setBookings] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [startHour, setStartHour] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endHour, setEndHour] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [availableInfo, setAvailableInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const allowsRentalQuantity = !!product?.allows_rental_quantity;
  const maxRentalQty = allowsRentalQuantity
    ? Math.max(1, Number(product?.rental_stock_qty || 1))
    : 1;

  useEffect(() => {
    if (product?.id) {
      fetchBookings(product.id)
        .then(setBookings)
        .catch(() => setBookings([]));
    }
  }, [product?.id, fetchBookings]);

  useEffect(() => {
    setQuantity(1);
    setAvailableInfo(null);
    setError("");
  }, [product?.id]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function isBlocked(dateObj) {
    if (allowsRentalQuantity) return false;

    for (const b of bookings) {
      const from = parseLocalDateTime(b.from);
      const toWithBuffer = new Date(
        parseLocalDateTime(b.to).getTime() + BUFFER_MILLIS
      );

      if (dateObj >= from && dateObj < toWithBuffer) return true;
    }

    return false;
  }

  function availableHoursForDay(date, _compareHour = null, isStart = true) {
    if (!date) return [];

    const now = new Date();

    return HOURS.filter((hr) => {
      const dt = getHourDate(date, hr);

      if (dt < now) return false;
      if (isBlocked(dt)) return false;

      if (isStart && endDate && endHour) {
        const endDt = getHourDate(endDate, endHour);
        if (dt >= endDt) return false;
      }

      if (!isStart && startDate && startHour) {
        const startDt = getHourDate(startDate, startHour);
        if (dt < new Date(startDt.getTime() + HOUR)) return false;
      }

      return true;
    });
  }

  function disabledDays(isStart) {
    const dayCheck = (date) =>
      availableHoursForDay(date, null, isStart).length === 0;
    return [dayCheck, { before: today }];
  }

  const priceDay = Number(product?.price_day ?? product?.pricePerDay ?? 0) || 0;
  const priceShort4h =
    product?.price_short_4h != null ? Number(product.price_short_4h) : null;
  const priceWeekend =
    product?.price_weekend != null ? Number(product.price_weekend) : null;

  let display = null;

  if (startDate && startHour && endDate && endHour) {
    const s = getHourDate(startDate, startHour);
    const e = getHourDate(endDate, endHour);
    const hours = (e - s) / HOUR;

    if (hours < 5 && priceShort4h != null) {
      display = {
        label: "≤ 4 timer",
        amount: priceShort4h,
        kind: "short",
        units: 1,
      };
    } else {
      const days = ceilDiv(hours, 24);
      const dayAmount = days * priceDay;

      if (priceWeekend != null && isWeekendPackage(s, e)) {
        const weekendAmount = priceWeekend;
        const cheapest = Math.min(weekendAmount, dayAmount);

        display = {
          label:
            cheapest === weekendAmount
              ? "Weekend (billigst)"
              : `${days} døgn (billigst)`,
          amount: cheapest,
          kind: cheapest === weekendAmount ? "weekend" : "day",
          units: cheapest === weekendAmount ? 1 : days,
        };
      } else {
        display = {
          label: `${days} døgn`,
          amount: dayAmount,
          kind: "day",
          units: days,
        };
      }
    }
  }

  const safeQty = allowsRentalQuantity
    ? Math.max(1, Math.min(maxRentalQty, Number(quantity || 1)))
    : 1;

  const totalAmount = display ? Number(display.amount || 0) * safeQty : 0;
  const hasCompletePeriod = !!(startDate && startHour && endDate && endHour && display);

  useEffect(() => {
    if (!hasCompletePeriod || !product?.id) {
      setAvailableInfo(null);
      return;
    }

    const startDt = getHourDate(startDate, startHour);
    const endDt = getHourDate(endDate, endHour);

    if (endDt <= startDt) {
      setAvailableInfo(null);
      return;
    }

    const timer = setTimeout(async () => {
      const toLocal = (d) => d.toLocaleDateString("sv-SE");
      const fromStr = `${toLocal(startDate)}T${startHour}`;
      const toStr = `${toLocal(endDate)}T${endHour}`;

      try {
        setChecking(true);
        const result = await checkAvailability({
          productId: product.id,
          from: fromStr,
          to: toStr,
          quantity: safeQty,
        });

        setAvailableInfo(result);
        if (!result?.available) {
          setError(result?.error || "Valgt leieperiode er ikke tilgjengelig.");
        } else {
          setError("");
        }
      } catch (err) {
        setAvailableInfo(null);
        setError(err.message || "Kunne ikke sjekke tilgjengelighet.");
      } finally {
        setChecking(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [
    product?.id,
    startDate,
    startHour,
    endDate,
    endHour,
    safeQty,
    hasCompletePeriod,
    checkAvailability,
  ]);

  async function handleAdd() {
    if (!startDate || !endDate || !startHour || !endHour) {
      setError("Velg både hentetid og tilbakelevering.");
      return;
    }

    if (!display) {
      setError("Kunne ikke beregne pris.");
      return;
    }

    const startDt = getHourDate(startDate, startHour);
    const endDt = getHourDate(endDate, endHour);

    if (endDt <= startDt) {
      setError("Tilbakelevering må være etter hentetid.");
      return;
    }

    const toLocal = (d) => d.toLocaleDateString("sv-SE");
    const fromStr = `${toLocal(startDate)}T${startHour}`;
    const toStr = `${toLocal(endDate)}T${endHour}`;

    try {
      setChecking(true);
      setError("");

      const result = await checkAvailability({
        productId: product.id,
        from: fromStr,
        to: toStr,
        quantity: safeQty,
      });

      setAvailableInfo(result);

      if (!result?.available) {
        setError(result?.error || "Valgt leieperiode er ikke tilgjengelig.");
        return;
      }

      onAddToCart({
        date_from: fromStr,
        date_to: toStr,
        price: display.amount,
        quantity: safeQty,
        pricing_kind: display.kind,
        pricing_units: display.units,
      });
    } catch (err) {
      setError(err.message || "Kunne ikke sjekke tilgjengelighet.");
    } finally {
      setChecking(false);
    }
  }

  const disabledDayStyle = {
    opacity: 0.35,
    background: "#f3f4f6",
    color: "#a3a3a3",
    cursor: "not-allowed",
    border: "none",
  };

  const unavailable = hasCompletePeriod && availableInfo && !availableInfo.available;

  return (
    <div className="scale-[0.90]">
      <h3 className="text-xl font-bold mb-3 text-[#000099]">Velg leieperiode</h3>

      <div className="flex mb-4 gap-6">
        <div className="flex-1">
          <div className="font-semibold mb-2">Hente</div>
          <div className="rounded-xl p-2">
            <DayPicker
              selected={startDate}
              onSelect={(date) => {
                setStartDate(date);
                setStartHour(null);
                setAvailableInfo(null);
                if (!endDate || (endDate && date > endDate)) {
                  setEndDate(date);
                  setEndHour(null);
                }
              }}
              disabled={disabledDays(true)}
              mode="single"
              locale={nb}
              className="w-full"
              style={{ background: "transparent", borderRadius: "0.75rem" }}
              modifiersStyles={{ disabled: disabledDayStyle }}
            />
          </div>

          {startDate && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {availableHoursForDay(startDate, endHour, true).map((hr) => (
                <button
                  key={hr}
                  className={`rounded px-2 py-1 border ${
                    startHour === hr ? "bg-[#000099] text-white" : "bg-white"
                  } hover:bg-[#c7d2fe]`}
                  onClick={() => {
                    setStartHour(hr);
                    setAvailableInfo(null);
                  }}
                  type="button"
                >
                  {hr}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1">
          <div className="font-semibold mb-2">Levere tilbake</div>
          <div className="rounded-xl p-2">
            <DayPicker
              selected={endDate}
              onSelect={(date) => {
                setEndDate(date);
                setEndHour(null);
                setAvailableInfo(null);
                if (!startDate || (startDate && date < startDate)) {
                  setStartDate(date);
                  setStartHour(null);
                }
              }}
              disabled={disabledDays(false)}
              mode="single"
              locale={nb}
              className="w-full"
              style={{ background: "transparent", borderRadius: "0.75rem" }}
              modifiersStyles={{ disabled: disabledDayStyle }}
            />
          </div>

          {endDate && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              {availableHoursForDay(endDate, startHour, false).map((hr) => (
                <button
                  key={hr}
                  className={`rounded px-2 py-1 border ${
                    endHour === hr ? "bg-[#000099] text-white" : "bg-white"
                  } hover:bg-[#c7d2fe]`}
                  onClick={() => {
                    setEndHour(hr);
                    setAvailableInfo(null);
                  }}
                  type="button"
                >
                  {hr}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {allowsRentalQuantity && (
        <div className="mb-4 max-w-xs mx-auto">
          <label className="block text-sm font-semibold mb-1 text-center">
            Antall
          </label>
          <input
            type="number"
            min={1}
            max={maxRentalQty}
            value={quantity}
            onChange={(e) => {
              const next = Math.max(
                1,
                Math.min(maxRentalQty, Number(e.target.value || 1))
              );
              setQuantity(next);
              setAvailableInfo(null);
              setError("");
            }}
            className="w-full border rounded px-3 py-2 text-center"
          />
          <div className="text-xs text-gray-500 text-center mt-1">
            Maks {maxRentalQty} stk
            {availableInfo?.availableQty != null
              ? ` · Ledig i valgt periode: ${availableInfo.availableQty} stk`
              : ''}
          </div>
        </div>
      )}

      <div className="text-center font-semibold mb-3">
        {display && (
          <>
            Valgt periode: {startDate.toLocaleDateString("no-NO")} kl {startHour} –{" "}
            {endDate.toLocaleDateString("no-NO")} kl {endHour}
            <br />
            <span className="text-lg font-bold text-[#000099]">
              {display.label}: {display.amount.toLocaleString("no-NO")} kr
              {allowsRentalQuantity && (
                <>
                  {" "}× {safeQty} stk = {totalAmount.toLocaleString("no-NO")} kr
                </>
              )}
            </span>
          </>
        )}
      </div>

      {checking && (
        <div className="text-gray-500 text-center mb-2">
          Sjekker tilgjengelighet...
        </div>
      )}

      {availableInfo?.available && hasCompletePeriod && (
        <div className="text-green-700 text-center mb-2">
          Tilgjengelig
          {availableInfo.availableQty != null
            ? ` – ${availableInfo.availableQty} stk ledig`
            : ''}
        </div>
      )}

      {error && <div className="text-red-600 text-center mb-2">{error}</div>}

      <button
        className="bg-[#000099] hover:bg-blue-900 text-white font-bold py-2 px-6 rounded transition block mx-auto disabled:opacity-50"
        onClick={handleAdd}
        disabled={!display || checking || unavailable}
      >
        Legg i handlekurv
      </button>
    </div>
  );
}